import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { TopBar, Icon, RailMenu, PhoneActionsContext, PhoneScreen } from '@/components/kit';
import { MaieRail } from '@/layouts/MaieRail';
import { useNarrowViewport, useShellChromeValue } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import type { SidebarItem } from '@/config/navigation';
import { useAuthStore } from '@/stores/useAuthStore';
import { useUnreadCount } from '@/hooks/useNotifications';
/* Built by the doctor lane for the same ▾ on Today's sub line; it is the one
   clinic switch in the app, so the shell's account menu reuses it. */
import { ClinicPicker } from '@/pages/doctor/today/ClinicPicker';
import { ShellPhoneFallback } from './ShellPhoneFallback';

/**
 * A phone pane is its own screen, so the browser's Back must close it rather
 * than leave the app: opening pushes one history entry, popping it closes the
 * pane, and the strip's own back pops it too (so no stale entry is left).
 */
function usePaneHistory(open: boolean, close?: () => void) {
  const pushed = useRef(false);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState(window.history.state, '');
    pushed.current = true;
    const onPop = () => {
      if (!pushed.current) return;
      pushed.current = false;
      closeRef.current?.();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      pushed.current = false;
    };
  }, [open]);
  return () => {
    if (pushed.current) window.history.back();
    else closeRef.current?.();
  };
}

/**
 * THE APP SHELL, in the design — rail, top bar and docked pane, on every route.
 *
 * This replaces the sidebar-and-header shell rather than sitting beside it.
 * That distinction is the whole point: a shell that only wraps the ported
 * screens means the product flips between two designs as you navigate, and
 * every route that has not been converted yet advertises that fact.
 *
 * HOW LEGACY PAGES SURVIVE IT. kit.css opens with
 * `.maie * { box-sizing: border-box; margin: 0; padding: 0 }`, so anything
 * inside a `.maie` subtree loses its spacing. The chrome is therefore scoped as
 * two SEPARATE `.maie` islands — the rail, and the top bar — with the flex
 * layout and the content area as plain elements between them. An un-ported page
 * renders exactly as it did; a ported one renders kit markup and opts into the
 * scope itself.
 *
 * This is what lets the migration be page-by-page instead of all-at-once: the
 * frame stops changing, and only what is inside it does.
 */
export function MaieShell({
  items,
  children,
  fullBleed = false,
}: {
  /** The real nav — same items, gates and destinations as before. */
  items: SidebarItem[];
  children: ReactNode;
  /** Surfaces that own their own height and scrolling (the assistant). */
  fullBleed?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const chrome = useShellChromeValue();
  const { person, branch } = useShellIdentity();
  /* `branch` carries the design's ▾ affordance, which belongs on a control,
     not on a heading. The bar's title takes the plain name. */
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const logout = useAuthStore((s) => s.logout);
  const availableTenants = useAuthStore((s) => s.availableTenants);
  /* THE DOT WAS PAINTED ON EVERY BOARD AND ON EVERY SCREEN. A bell that always
     claims something is waiting teaches people to ignore it, which is the one
     thing a bell must not do. It follows the real count now. */
  const { data: unread } = useUnreadCount();
  const narrow = useNarrowViewport();
  const closePane = usePaneHistory(Boolean(narrow && chrome.pane && chrome.paneOpen), chrome.onPaneClose);

  /* A page that has not been ported publishes no chrome, and an empty title bar
     reads as a broken shell rather than an un-migrated page. The nav already
     knows what this route is called — including inside a folded group — so the
     bar falls back to that. Longest match wins, so /patients/:id keeps the
     Patients label rather than falling through to nothing. */
  const navTitle = useMemo(() => {
    const flat = items.flatMap((i) => [i, ...(i.children ?? [])]);
    const hit = flat
      .filter((i) => i.path && (location.pathname === i.path || location.pathname.startsWith(`${i.path}/`)))
      .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0];
    return hit?.label ?? '';
  }, [items, location.pathname]);

  /* A page that drew its phone layout gets the whole screen below the desk
     breakpoint. See ShellChrome.phone. The routed page stays MOUNTED, hidden:
     it is what publishes the phone layout, and unmounting it would withdraw
     the layout it just published. */
  if (narrow) {
    /* A page that drew its own phone screen gets it. One that has not is NOT
       given the desk shell at 390px — a 210px rail eating half the width with
       the pane clipped off the edge is a broken page, not an un-ported one.
       It gets its own content, full width, under the same top strip, with ☰
       opening the rail's own doors. */
    /* A SELECTION IS ITS OWN SCREEN ON A PHONE (see ShellChrome.paneOpen).
       The list underneath stays mounted — it is what publishes the chrome,
       and keeping it means back lands on the same scroll position. */
    const paneScreen =
      chrome.pane && chrome.paneOpen ? (
        <PhoneScreen
          className="phone-pane-screen"
          crumb={chrome.paneTitle ? chrome.panePhoneCrumb ?? chrome.phoneTitle ?? (chrome.title || navTitle) : undefined}
          title={chrome.paneTitle ?? (chrome.title || navTitle)}
          onBack={closePane}
          menu={chrome.paneMenu ?? chrome.phoneMenu}
          /* An empty slot a pane may portal its header chip into (data-pane-trailing). */
          trailing={<span data-pane-trailing style={{ display: 'contents' }} />}
        >
          <div className="maie desk phone-pane">{chrome.pane}</div>
        </PhoneScreen>
      ) : null;
    const inlinePane =
      !chrome.phone && chrome.pane && chrome.paneInline ? (
        <div className="maie desk phone-pane phone-pane-inline">{chrome.pane}</div>
      ) : null;

    /* The page's verbs travel with it: the fallback draws them in its strip,
       and a page's own PhoneScreen can ask for them (`actions`). */
    return (
      <PhoneActionsContext.Provider value={{ primary: chrome.primary, secondary: chrome.secondary }}>
      <div className="h-screen-safe" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        {paneScreen}
        <div style={{ height: '100%', display: paneScreen ? 'none' : undefined }}>
          {chrome.phone ?? (
            <ShellPhoneFallback
              items={items}
              title={chrome.title || navTitle}
              sub={chrome.sub}
              menu={chrome.phoneMenu}
              chrome={chrome}
            >
              {children}
              {inlinePane}
            </ShellPhoneFallback>
          )}
        </div>
        {/* The routed page stays MOUNTED when it published a layout: it is what
            publishes it, and unmounting would withdraw what it just gave us. */}
        {chrome.phone ? <div hidden>{children}</div> : null}
      </div>
      </PhoneActionsContext.Provider>
    );
  }

  return (
    <div className="flex h-screen-safe" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Chrome island 1 — the rail. */}
      <MaieRail items={items} onOpenMenu={() => setMenuOpen(true)} />

      {/* THE FOOT IS THE ACCOUNT CONTROL, and it had nowhere to go: it
          navigated to /settings, which is the CLINIC's settings and refuses
          everyone but the owner and the manager — so on this shell there was
          no way to sign out at all. The kit already carries the menu the
          canvases draw (RailMenu); it just was not wired. Only the items that
          lead somewhere real are listed: there is no branch model yet, so
          "Switch branch" is not offered, and the clinic switch appears only
          for a login that works at more than one. */}
      {/* Modal portals itself into its own `.maie` island (kit/Modal.tsx). */}
      <RailMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        items={[
          {
            key: 'account',
            label: 'Account — profile · signature · preferences',
            /* `/settings` IS the personal one — profile, security, appearance,
               with hash tabs. `/profile` is not a route at all: the page of
               that name is a CHILD of `clinic` (`/clinic/profile`, the admin's
               System Settings), so this menu item fell through to the
               catch-all and landed on the clinic dashboard. */
            onSelect: () => { setMenuOpen(false); navigate('/settings'); },
          },
          ...(availableTenants.length > 1
            ? [{
                key: 'clinic',
                label: 'Switch clinic',
                onSelect: () => { setMenuOpen(false); setPickerOpen(true); },
              }]
            : []),
          {
            key: 'signout',
            label: 'Sign out',
            onSelect: () => {
              setMenuOpen(false);
              void logout().finally(() => navigate('/login', { replace: true }));
            },
          },
        ]}
      />
      <ClinicPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />

      {/* Chrome island 1b — the section SubRail, when a screen has one. It sits
          between the rail and the main column, which is where the canvas draws
          it: the top bar belongs to the section, not to the whole app, so it
          starts to the RIGHT of this column. */}
      {chrome.subrail ? (
        <div className="maie desk kit-subrail-dock">{chrome.subrail}</div>
      ) : null}

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Chrome island 2 — the top bar. A page declares what it says; a page
            that declares nothing still gets a bar, because the shell is what
            stays put. */}
        <div className="maie desk" style={{ flexShrink: 0 }}>
          {/* WHAT THE BAR IS FOR DEPENDS ON WHETHER THE PAGE HAS A HEADER.

              A ported page draws no heading of its own — this bar IS its
              heading, which is why it publishes a title. An un-ported page
              brings its own H1, and repeating it here printed the same words
              twice, ninety pixels apart, at the same left edge: not two
              regions, just a screen that looks broken.

              So on an un-ported route the bar stops competing and says WHERE
              YOU ARE instead — the clinic, then the role and the section. The
              page's own heading is then the only heading on screen, and the
              shell keeps the constant identity a shell is for. */}
          <TopBar
            title={chrome.title ?? branchName}
            sub={
              chrome.sub ??
              <>{person?.role ? `${person.role} · ` : ''}{navTitle || branchName}</>
            }
            search={chrome.search ?? null}
            onSearch={chrome.onSearch}
            searchValue={chrome.searchValue}
            onSearchChange={chrome.onSearchChange}
            searchSize={chrome.searchSize}
            onBell={chrome.onBell ?? (() => navigate('/notifications'))}
            hasUnread={(unread ?? 0) > 0}
            extra={chrome.extra}
            primary={chrome.primary}
            secondary={chrome.secondary}
          />
        </div>

        {/* CONTENT — deliberately outside every `.maie` scope. See the header. */}
        <main
          id="main"
          tabIndex={-1}
          className={`flex-1 focus:outline-none ${fullBleed ? 'overflow-hidden' : 'overflow-auto'}`}
        >
          {children}
        </main>
      </div>

      {/* Chrome island 3 — the docked pane, when the page has one to dock. */}
      {chrome.pane ? <div className="maie desk kit-pane-dock">{chrome.pane}</div> : null}
    </div>
  );
}

/** The skip link, kept from the previous shell — it is an accessibility floor. */
export function SkipToContent({ isRTL }: { isRTL: boolean }) {
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        backgroundColor: 'var(--color-primary-600)',
        color: 'var(--color-text-button)',
      }}
    >
      {isRTL ? 'تجاوز إلى المحتوى' : 'Skip to main content'}
      <Icon name="arrow-right" className="ic-inline" />
    </a>
  );
}

/**
 * The shell with the routed page inside it.
 *
 * Lives here rather than in MainLayout because it CONSUMES the chrome a page
 * publishes, and the provider is mounted one level up — a component cannot read
 * a context it is itself providing.
 *
 * Two content modes, and the difference matters:
 *
 *   legacy  wrapped in the `max-w-7xl` page container. That container is what
 *           gives ~70 un-ported pages one width; before it existed they carried
 *           three different ones between them.
 *   raw     the page draws kit markup and brings its own `.dcontent`. Nesting
 *           the legacy container inside that double-pads every design screen.
 */
export function MaieShellBody({
  items,
  isFullBleed,
  resetKey,
}: {
  items: SidebarItem[];
  isFullBleed: boolean;
  resetKey: string;
}) {
  const chrome = useShellChromeValue();
  const location = useLocation();

  const page = (
    <ErrorBoundary resetKey={resetKey}>
      <Outlet />
    </ErrorBoundary>
  );

  return (
    <MaieShell items={items} fullBleed={isFullBleed || Boolean(chrome.raw)}>
      {isFullBleed ? (
        <div className="h-full">{page}</div>
      ) : chrome.raw ? (
        page
      ) : (
        /* ALIGNED TO THE CHROME, NOT CENTRED IN IT.
           `mx-auto max-w-7xl` gave ~70 un-ported pages one width, which was
           right under a full-bleed header and is wrong under this one: the top
           bar's title sits at a definite 36px, and a centred container put the
           page's own heading 500px to the right of it on a wide screen. Two
           left edges on one screen, and the content reading as though it had
           come loose from its header.

           So: same gutter as `.dcontent` and `.topbar` (36px at desk), anchored
           left. The cap stays — a legacy form stretched across 2300px is worse
           than one that ends — but it now hangs off the same edge as the
           chrome instead of floating in the middle. */
        <div
          className="w-full max-w-7xl px-4 sm:px-6 lg:px-9 py-4 sm:py-6"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 7rem)' }}
          key={location.pathname}
        >
          {page}
        </div>
      )}
    </MaieShell>
  );
}
